// Single frontend entrypoint (Vite).
//
// Migration strategy (production-grade, incremental):
// 1) Introduce /src structure + build + linting + env/versioning.
// 2) Migrate page-by-page to modules (no framework, behavior 1:1).
// 3) Dashboard still has a transitional runtime module for shared boot/utils,
//    but it is no longer build-generated.

import { ENV } from './utils/env.js';
import { initTheme } from './ui/theme.js';
import QRCodeLib from 'qrcode';

globalThis.App = globalThis.App || {};
globalThis.App.build = {
  version: ENV.appVersion,
  time: ENV.buildTime,
  mode: ENV.mode,
};

function isLoginPage() {
  return !!document.getElementById('loginForm');
}

function isDashboardPage() {
  // Dashboard has a sidebar and a title header; login doesn't.
  return !!document.getElementById('sidebar') && !!document.getElementById('pageTitle');
}

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getLoginRedirectTarget() {
  const currentPath = String(window.location.pathname || '').trim();
  if (currentPath === '/owner/billing') return '/owner/billing';
  return '/dashboard';
}

const LOGIN_FINGERPRINT_STORAGE_KEY = 'morvi_login_fingerprint_v1';
const CAPTCHA_CONFIG_URL = '/api/onboarding/captcha-config';
const DEFAULT_CAPTCHA_SCRIPT_URL = 'https://smartcaptcha.cloud.yandex.ru/captcha.js';
let captchaScriptPromise = null;

function getLoginFingerprint() {
  const fallback = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  if (typeof window === 'undefined') return fallback;

  try {
    const existing = window.localStorage.getItem(LOGIN_FINGERPRINT_STORAGE_KEY);
    if (existing) return existing;

    const next = (window.crypto && typeof window.crypto.randomUUID === 'function')
      ? window.crypto.randomUUID()
      : fallback;

    window.localStorage.setItem(LOGIN_FINGERPRINT_STORAGE_KEY, next);
    return next;
  } catch {
    return fallback;
  }
}

function loadCaptchaScript(scriptUrl) {
  if (window.smartCaptcha && typeof window.smartCaptcha.render === 'function') {
    return Promise.resolve();
  }

  if (captchaScriptPromise) return captchaScriptPromise;

  captchaScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl || DEFAULT_CAPTCHA_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Не удалось загрузить SmartCaptcha'));
    document.head.appendChild(script);
  });

  return captchaScriptPromise;
}

async function bootDashboard() {
  globalThis.App = globalThis.App || {};
  globalThis.App.dashboardShellEnabled = true;
  globalThis.App.dashboardFlags = {
    ...(globalThis.App.dashboardFlags || {}),
    useModularNotifications: true,
    useModularSocket: true,
  };

  // Transitional runtime with shared dashboard boot/utils.
  await import('./legacy/dashboard-runtime.js');

  const { initDashboardShell } = await import('./features/dashboard-shell/index.js');
  initDashboardShell();

  const { initCoursePlayerTabs } = await import('./features/course-player/tabs.js');
  initCoursePlayerTabs();

  const { initCoursesFeature } = await import('./features/courses.js');
  initCoursesFeature();

  const { initCrmTicketsFeature } = await import('./features/crmTickets.js');
  initCrmTicketsFeature();

  const { initCourseEditorQuestionActions } = await import('./features/course-editor/question-actions.js');
  initCourseEditorQuestionActions();

  const { initKbEditorToolbar } = await import('./features/kb/editor-toolbar.js');
  initKbEditorToolbar();

  const { initKbFeature } = await import('./features/kb/index.js');
  initKbFeature();

  const { initDashboardDynamicHandlers } = await import('./features/dashboard/dynamic-handlers.js');
  initDashboardDynamicHandlers();

  const { initWarehouseFeature } = await import('./features/warehouse.js');
  initWarehouseFeature();

  const { initMarginFeature } = await import('./features/margin.js');
  initMarginFeature();

  const { initWarehouseTableSort } = await import('./features/warehouse/table-sort.js');
  initWarehouseTableSort();

  const { initWarehouseActionButtons } = await import('./features/warehouse/action-buttons.js');
  initWarehouseActionButtons();

  const { initScheduleFeature } = await import('./features/schedule.js');
  initScheduleFeature();

  const { initScheduleShiftModal } = await import('./features/schedule/shift-modal.js');
  initScheduleShiftModal();

  const { initSalaryFeature } = await import('./features/salary.js');
  initSalaryFeature();

  const { initSalaryEditActions } = await import('./features/salary/edit-actions.js');
  initSalaryEditActions();

  const { initUsersFeature } = await import('./features/users.js');
  initUsersFeature();

  const { initLogsFeature } = await import('./features/logs.js');
  initLogsFeature();

  const { initTasksHub } = await import('./features/tasks/index.js');
  initTasksHub();

  // notifications disabled
  // const { initDashboardNotifications } = await import('./features/dashboard-notifications.js');
  // initDashboardNotifications();

  const { setupDashboardSocketHandlers } = await import('./socket/socket.js');
  setupDashboardSocketHandlers();
}

function bootLogin() {
  // 1) Service Worker
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(console.error);
  }

  // 2) Theme
  initTheme();

  // 3) QR login logic (auto refresh)
  initQRLogin();

  const fingerprint = getLoginFingerprint();
  const captchaWrap = document.getElementById('loginCaptchaWrap');
  let captchaConfigPromise = null;
  let captchaRequired = false;
  let captchaWidgetId = null;
  let captchaToken = '';

  const setLoginError = (message) => {
    const errEl = document.getElementById('error');
    if (errEl) errEl.textContent = message || '';
  };

  const getCaptchaConfig = async () => {
    if (captchaConfigPromise) return captchaConfigPromise;
    captchaConfigPromise = fetch(CAPTCHA_CONFIG_URL, { cache: 'no-store' })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { enabled: false };
        return {
          enabled: Boolean(data?.enabled),
          siteKey: data?.siteKey || '',
          scriptUrl: data?.scriptUrl || DEFAULT_CAPTCHA_SCRIPT_URL,
        };
      })
      .catch(() => ({ enabled: false }));
    return captchaConfigPromise;
  };

  const ensureCaptchaVisible = async () => {
    const config = await getCaptchaConfig();
    if (!config.enabled || !config.siteKey || !captchaWrap) return false;

    captchaRequired = true;
    captchaWrap.style.display = 'block';

    await loadCaptchaScript(config.scriptUrl);
    if (!window.smartCaptcha || typeof window.smartCaptcha.render !== 'function') return false;

    if (captchaWidgetId == null) {
      captchaWidgetId = window.smartCaptcha.render('loginCaptcha', {
        sitekey: config.siteKey,
        hl: 'ru',
        callback: (token) => {
          captchaToken = String(token || '').trim();
        },
        'expired-callback': () => {
          captchaToken = '';
        },
        'error-callback': () => {
          captchaToken = '';
        },
      });
    } else if (typeof window.smartCaptcha.reset === 'function') {
      window.smartCaptcha.reset(captchaWidgetId);
    }

    return true;
  };

  const resetCaptcha = () => {
    captchaToken = '';
    if (captchaWidgetId == null) return;
    if (!window.smartCaptcha || typeof window.smartCaptcha.reset !== 'function') return;
    window.smartCaptcha.reset(captchaWidgetId);
  };

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('auth_retry')) {
    console.warn('⚠️ Обнаружен цикличный редирект.');
    window.history.replaceState({}, document.title, window.location.pathname);
    return;
  }

  // 4) Auto-login
  const loginBtn = document.querySelector('button[type="submit"]');
  if (loginBtn) loginBtn.textContent = 'Вход...';

  fetch('/api/me', { credentials: 'include' })
    .then(async (res) => {
      if (!res.ok) throw new Error(`Auth check failed: ${res.status}`);
      window.location.href = getLoginRedirectTarget();
    })
    .catch(() => {
      if (loginBtn) loginBtn.textContent = 'Войти';
    });

  void getCaptchaConfig();

  // 5) Login form
  const form = document.getElementById('loginForm');
  if (form) {
    // Keep legacy behavior: use onsubmit assignment.
    form.onsubmit = async (e) => {
      e.preventDefault();
      setLoginError('');

      const username = document.getElementById('username')?.value?.trim() || '';
      const password = document.getElementById('password')?.value || '';
      const payload = { username, password, fingerprint };

      if (captchaRequired) {
        if (!captchaToken) {
          setLoginError('Подтвердите, что вы не робот');
          return;
        }
        payload.captcha_token = captchaToken;
      }

      try {
        const res = await fetch('/api/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-device-fingerprint': fingerprint,
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          if (data?.captchaRequired) {
            const wasCaptchaRequired = captchaRequired;
            const shown = await ensureCaptchaVisible().catch(() => false);
            captchaRequired = shown || captchaRequired;
            if (!shown) {
              setLoginError('Не удалось загрузить капчу. Обновите страницу и повторите попытку.');
              return;
            }
            if (wasCaptchaRequired) resetCaptcha();
          }
          setLoginError(data.message || 'Ошибка входа');
          return;
        }

        window.location.href = getLoginRedirectTarget();
      } catch (err) {
        console.error(err);
        setLoginError('Сервер недоступен');
      }
    };
  }
}

// === QR Login (Socket.IO + QRCode.js) ===
let loginSocket = null;
let qrInterval = null;

async function renderQrCode(container, text) {
  container.innerHTML = '';

  const canvas = document.createElement('canvas');
  canvas.width = 260;
  canvas.height = 260;

  await QRCodeLib.toCanvas(canvas, String(text || ''), {
    width: 260,
    margin: 1,
    errorCorrectionLevel: 'H',
    color: {
      dark: '#000000',
      light: '#ffffff',
    },
  });

  container.appendChild(canvas);
}

function initQRLogin() {
  if (loginSocket) return;
  if (typeof io === 'undefined') return;
  loginSocket = io();

  const refreshQR = async () => {
    const currentQrId = generateUUID();

    const qrEl = document.getElementById('qrcode');
    if (!qrEl) return;

    try {
      await renderQrCode(qrEl, currentQrId);
    } catch (error) {
      // Fallback to plain text to keep login flow alive when QR library is unavailable.
      qrEl.textContent = currentQrId;
      qrEl.style.wordBreak = 'break-all';
      qrEl.style.padding = '12px';
      qrEl.style.textAlign = 'center';
      console.error('QR render failed:', error && error.message ? error.message : error);
    }

    loginSocket.emit('join_qr_room', currentQrId);
    console.log('QR refreshed:', currentQrId);
  };

  refreshQR();
  qrInterval = setInterval(refreshQR, 15000);

  loginSocket.on('qr_login_success', async (data) => {
    if (qrInterval) clearInterval(qrInterval);

    const statusEl = document.getElementById('qrStatus');
    if (statusEl) {
      statusEl.textContent = 'Успех! Входим...';
      statusEl.style.color = '#2ecc71';
      statusEl.style.fontWeight = 'bold';
    }

    try {
      await fetch('/api/qr-claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ qrId: data.qrId }),
        credentials: 'include',
      });
    } catch (e) {
      console.error('Cookie set failed', e);
    }

    setTimeout(() => {
      window.location.href = getLoginRedirectTarget();
    }, 500);
  });
}

// Note: in production builds Vite may place the module script in <head>.
// Avoid checking for DOM elements at module-eval time; decide the page at DOM ready.
function boot() {
  if (isLoginPage()) bootLogin();
  if (isDashboardPage()) bootDashboard().catch(console.error);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
